'use client';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { supabase } from '@/lib/supabase';
import CustomerDashboardLayout from '../components/CustomerDashboardLayout';

interface LoyaltyTransaction {
  id: string;
  points: number;
  transaction_type: string;
  description: string;
  created_at: string;
}

export default function CustomerLoyaltyPage() {
  const router = useRouter();
  const [loyaltyPoints, setLoyaltyPoints] = useState(0);
  const [lifetimePoints, setLifetimePoints] = useState(0);
  const [transactions, setTransactions] = useState<LoyaltyTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getUser().then(async ({ data: { user } }) => {
      if (!user) { router.push('/customer-login'); return; }

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('loyalty_points, lifetime_points')
        .eq('id', user.id)
        .maybeSingle();

      if (profile) {
        setLoyaltyPoints(profile.loyalty_points || 0);
        setLifetimePoints(profile.lifetime_points || 0);
      }

      const { data: txns } = await supabase
        .from('customer_loyalty_transactions')
        .select('*')
        .eq('customer_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      setTransactions(txns || []);
      setLoading(false);
    });
  }, [router]);

  const tiers = [
    { name: 'Bronze', min: 0, max: 999, color: 'text-amber-700 bg-amber-100', icon: 'StarIcon' },
    { name: 'Silver', min: 1000, max: 4999, color: 'text-slate-600 bg-slate-100', icon: 'StarIcon' },
    { name: 'Gold', min: 5000, max: 9999, color: 'text-yellow-600 bg-yellow-100', icon: 'TrophyIcon' },
    { name: 'Platinum', min: 10000, max: Infinity, color: 'text-purple-600 bg-purple-100', icon: 'TrophyIcon' },
  ];

  const currentTier = tiers.find(t => lifetimePoints >= t.min && lifetimePoints <= t.max) || tiers[0];
  const nextTier = tiers[tiers.indexOf(currentTier) + 1];

  return (
    <CustomerDashboardLayout>
      <div className="space-y-6 max-w-2xl">
        <div>
          <h1 className="text-2xl font-700 text-slate-900">Loyalty Points</h1>
          <p className="text-sm text-slate-500 mt-0.5">Earn points on every purchase and redeem for discounts</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Icon name="ArrowPathIcon" size={28} className="animate-spin text-primary" />
          </div>
        ) : (
          <>
            {/* Points summary */}
            <div className="bg-gradient-to-r from-amber-400 to-amber-500 rounded-2xl p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/80">Available Points</p>
                  <p className="text-4xl font-700 mt-1">{loyaltyPoints.toLocaleString('en-IN')}</p>
                  <p className="text-sm text-white/70 mt-1">≈ ₹{(loyaltyPoints / 10).toLocaleString('en-IN')} value</p>
                </div>
                <div className={`px-4 py-2 rounded-xl ${currentTier.color} font-700 text-sm`}>
                  {currentTier.name} Member
                </div>
              </div>
            </div>

            {/* Tier progress */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h2 className="text-sm font-700 text-slate-900 mb-4">Membership Tier</h2>
              <div className="flex items-center gap-3 mb-4">
                {tiers.map((tier, i) => (
                  <div key={tier.name} className="flex-1 text-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-1 ${lifetimePoints >= tier.min ? tier.color : 'bg-slate-100 text-slate-400'}`}>
                      <Icon name={tier.icon as never} size={14} />
                    </div>
                    <p className="text-xs font-600 text-slate-600">{tier.name}</p>
                    <p className="text-xs text-slate-400">{tier.min.toLocaleString('en-IN')}+</p>
                  </div>
                ))}
              </div>
              {nextTier && (
                <div>
                  <div className="flex justify-between text-xs text-slate-500 mb-1">
                    <span>{currentTier.name}</span>
                    <span>{nextTier.name} at {nextTier.min.toLocaleString('en-IN')} pts</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-amber-400 rounded-full transition-all"
                      style={{ width: `${Math.min(100, ((lifetimePoints - currentTier.min) / (nextTier.min - currentTier.min)) * 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">{Math.max(0, nextTier.min - lifetimePoints).toLocaleString('en-IN')} points to {nextTier.name}</p>
                </div>
              )}
            </div>

            {/* How to earn */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h2 className="text-sm font-700 text-slate-900 mb-4">How to Earn Points</h2>
              <div className="space-y-3">
                {[
                  { action: 'Make a purchase', points: '1 point per ₹10 spent', icon: 'ShoppingBagIcon' },
                  { action: 'Write a review', points: '50 points', icon: 'StarIcon' },
                  { action: 'Refer a friend', points: '200 points', icon: 'UserPlusIcon' },
                  { action: 'Complete profile', points: '100 points', icon: 'UserCircleIcon' },
                ].map(item => (
                  <div key={item.action} className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name={item.icon as never} size={16} className="text-amber-500" />
                    </div>
                    <div>
                      <p className="text-sm font-500 text-slate-800">{item.action}</p>
                      <p className="text-xs text-slate-400">{item.points}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Transaction history */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100">
                <h2 className="text-sm font-700 text-slate-900">Points History</h2>
              </div>
              {transactions.length === 0 ? (
                <div className="py-12 text-center text-slate-400 text-sm">No transactions yet</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {transactions.map(txn => (
                    <div key={txn.id} className="px-5 py-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-500 text-slate-800">{txn.description}</p>
                        <p className="text-xs text-slate-400">{new Date(txn.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                      </div>
                      <span className={`text-sm font-700 ${txn.transaction_type === 'credit' ? 'text-green-600' : 'text-red-500'}`}>
                        {txn.transaction_type === 'credit' ? '+' : '-'}{Math.abs(txn.points)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </CustomerDashboardLayout>
  );
}
